import { Box, Typography, useTheme, Button, MenuItem, TextField, Menu } from "@mui/material";
import { useNavigate }  from "react-router-dom";
import { useSelector }  from "react-redux";
import { useState }     from "react";

import InventoryIcon    from "@mui/icons-material/Inventory";
import FilterListIcon   from "@mui/icons-material/FilterList";
import DownloadIcon     from "@mui/icons-material/Download";
import FormatListIcon   from "@mui/icons-material/FormatListBulleted";
import VisibilityIcon   from "@mui/icons-material/Visibility";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import { useItemTypeList, useNearlyOutOfStockList } from "api/hooks/inventory";
import { PDFDownloadLink }  from "@react-pdf/renderer";
import NearOutOfStockPdf    from "scenes/reports/pdfForms/inventory/nearOutOfStockReportPdf";

export default function NearOutOfStock() {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isDark   = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const user               = useSelector((s) => s.authReducer.user);
  const { nearlyOutOfStockList, isLoadingNearlyOutOfStockList, setSearchFilter } = useNearlyOutOfStockList(1, business_branch_id);
  const { typeList } = useItemTypeList(1, business_branch_id);

  const [exportAnchor, setExportAnchor] = useState(null);
  const exportOpen = Boolean(exportAnchor);

  const inputSx = { "& .MuiInputBase-root": { fontSize: "13px", height: "36px", backgroundColor: cardBg, borderRadius: "8px" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: border }, "& .MuiInputLabel-root": { fontSize: "12px" } };
  const menuItemSx = { fontSize: "13px", color: textPri, borderRadius: "6px", mx: "4px", "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" } };

  const columns = [
    { label: "#",             width: 40  },
    { label: "Item Name"                 },
    { label: "Description"               },
    { label: "Unit"                      },
    { label: "Reorder Level", align: "center" },
    { label: "Current Stock", align: "center" },
    { label: "Actions",       align: "center" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{ width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "rgba(186,117,23,0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "#BA7517", flexShrink: 0 }}>
            <InventoryIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>Near Out of Stock</Typography>
            <Typography sx={{ fontSize: "12px", color: "#BA7517", mt: "2px" }}>{nearlyOutOfStockList.length} items low on stock</Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: "0.5rem" }}>
          <Button onClick={() => navigate("/productLogList", { state: { productList: [] } })} startIcon={<FormatListIcon sx={{ fontSize: "16px" }} />}
            sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", border: `1px solid ${border}`, color: textSec, backgroundColor: "transparent", textTransform: "none", "&:hover": { backgroundColor: "rgba(29,158,117,0.06)", borderColor: accent, color: accent } }}>
            View All Logs
          </Button>
          <Button onClick={(e) => setExportAnchor(e.currentTarget)} endIcon={<DownloadIcon sx={{ fontSize: "16px" }} />}
            sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", border: `1px solid ${border}`, color: accent, backgroundColor: "rgba(29,158,117,0.06)", textTransform: "none", "&:hover": { backgroundColor: "rgba(29,158,117,0.12)" } }}>
            Export
          </Button>
          <Menu anchorEl={exportAnchor} open={exportOpen} onClose={() => setExportAnchor(null)}
            PaperProps={{ sx: { mt: "6px", minWidth: "160px", backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "10px", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" } }}>
            <MenuItem onClick={() => setExportAnchor(null)} sx={menuItemSx}>
              <PDFDownloadLink document={<NearOutOfStockPdf viewData={{ report_detail: nearlyOutOfStockList }} printedBy={`${user.first_name} ${user.last_name}`} />} fileName="Near Out of Stock Report.pdf" style={{ textDecoration: "none", color: "inherit" }}>Download PDF</PDFDownloadLink>
            </MenuItem>
            <MenuItem onClick={() => setExportAnchor(null)} sx={menuItemSx}>Download Excel</MenuItem>
          </Menu>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem", p: "0.75rem 1rem", backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "10px" }}>
        <FilterListIcon sx={{ fontSize: "16px", color: accent }} />
        <TextField label="Category" size="small" select defaultValue="all" onChange={(e) => setSearchFilter(e.target.value)} sx={{ ...inputSx, width: "200px" }}>
          <MenuItem value="all">All categories</MenuItem>
          {typeList.map((r) => <MenuItem key={r.id} value={r.id}>{r.category_name}</MenuItem>)}
        </TextField>
      </Box>

      <ReportTable columns={columns} rows={nearlyOutOfStockList} loading={isLoadingNearlyOutOfStockList}
        emptyMessage="No near out-of-stock items found"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{index + 1}</RCell>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.product_name}</Typography></RCell>
            <RCell>{row.description}</RCell>
            <RCell>{row.unit}</RCell>
            <RCell align="center">{row.reorder_level}</RCell>
            <RCell align="center">
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", px: "8px", py: "3px", borderRadius: "20px", backgroundColor: "rgba(186,117,23,0.1)" }}>
                <Box sx={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: "#BA7517", flexShrink: 0 }} />
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#BA7517" }}>{row.available_stock}</Typography>
              </Box>
            </RCell>
            <RCell align="center">
              <Box onClick={() => navigate("/productLogList", { state: { productList: [row] } })} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(29,158,117,0.3)", cursor: "pointer", color: accent, fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" } }}>
                <VisibilityIcon sx={{ fontSize: "14px" }} /> View Log
              </Box>
            </RCell>
          </RRow>
        )}
      />
    </Box>
  );
}